/// <reference path="../../types/window.d.ts" />

import { assertUtagDataExists } from './tealium-invariants';

/**
 * Utilities for managing Tealium's data layer (window.utag_data).
 *
 * The utag_data object is Tealium's global data layer that persists
 * page-level metadata. Tealium reads from this object when firing events.
 *
 * @see https://docs.tealium.com/platforms/javascript/install/#utag_data-object
 */

/**
 * Updates the global window.utag_data object with metadata.
 * Tealium continuously reads from this global, so it must stay in sync
 * with the current route and user state.
 *
 * Only updates fields that are provided - existing values are preserved.
 *
 * @param {object} metadata - Metadata to merge into utag_data
 * @param {string} [metadata.pageName] - Current page name (for page tracking)
 * @param {string} [metadata.userCategory] - User category/role (for user tracking)
 * @param {string} [metadata.userType] - User type (for user tracking)
 */
export function syncUtagData({
  pageName = '',
  userCategory = '',
  userType = '',
  ...additionalMetadata
}) {
  if (typeof window === 'undefined') {
    return;
  }

  // Assert utag_data exists (fails in dev, warns in prod)
  if (!assertUtagDataExists()) {
    return;
  }

  // Build update object - only include fields that have new values
  /** @type {Record<string, string>} */
  const updates = { ...additionalMetadata };

  if (pageName && typeof pageName === 'string' && pageName.trim() !== '') {
    updates.PageName = pageName;
  }

  if (userCategory && typeof userCategory === 'string') {
    updates.UserRoleName = userCategory;
  }

  if (userType && typeof userType === 'string') {
    updates.UserRole = userType;
  }

  // Merge updates into utag_data
  window.utag_data = {
    ...window.utag_data,
    ...updates,
  };
}
